import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { ArrowLeftIcon } from "./components/icons"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { Portal } from "./components/portal"
import { DetailPageSkeleton } from "./components/skeleton"

type ProgramIcon = "book" | "scissor" | "atom"
type ProgramStatus = "active" | "inactive"
type ParticipantStatus = "active" | "inactive" | "graduated"
type EventStatus = "draft" | "published" | "cancelled"
type EventType = "workshop" | "showcase" | "graduation" | "field_trip" | "other"

interface Program {
  id: string
  code: string
  name: string
  description: string | null
  icon: ProgramIcon
  status: ProgramStatus
  participantsCount: number
  createdAt: string
  updatedAt: string
}

interface ParticipantListItem {
  id: string
  code: string
  programId: string
  firstName: string
  lastName: string
  email: string | null
  cohort: string
  since: string
  attendancePercent: number
  notes: string | null
  status: ParticipantStatus
}

interface EventListItem {
  id: string
  code: string
  programId: string
  title: string
  type: EventType
  date: string
  startTime: string
  endTime: string | null
  venue: string | null
  status: EventStatus
}

const STATUS_COLORS: Record<ParticipantStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  inactive: { bg: "bg-gold-100", text: "text-gold-700" },
  graduated: { bg: "bg-blue-100", text: "text-blue-700" },
}

const PROGRAM_STATUS_COLORS: Record<ProgramStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  inactive: { bg: "bg-red-100", text: "text-red-700" },
}

const EVENT_STATUS_COLORS: Record<EventStatus, { bg: string; text: string }> = {
  draft: { bg: "bg-gold-100", text: "text-gold-700" },
  published: { bg: "bg-green-100", text: "text-green-700" },
  cancelled: { bg: "bg-red-100", text: "text-red-700" },
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  workshop: "Workshop",
  showcase: "Showcase",
  graduation: "Graduation",
  field_trip: "Field Trip",
  other: "Other",
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  )
}

function ScissorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  )
}

function AtomIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z" />
      <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function ProgramIcon({ icon, className }: { icon: ProgramIcon; className?: string }) {
  switch (icon) {
    case "book":
      return <BookIcon className={className} />
    case "scissor":
      return <ScissorIcon className={className} />
    case "atom":
      return <AtomIcon className={className} />
    default:
      return <BookIcon className={className} />
  }
}

export function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("programs")
  const canMutate = useHasPermission("programs_mutate")
  const [program, setProgram] = useState<Program | null>(null)
  const [participants, setParticipants] = useState<ParticipantListItem[]>([])
  const [events, setEvents] = useState<EventListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    icon: "book" as ProgramIcon,
    status: "active" as ProgramStatus,
  })

  // Add participant form state
  const [participantForm, setParticipantForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    cohort: "",
    since: "",
    attendancePercent: "0",
    notes: "",
  })

  const fetchProgram = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.programs[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        const p = body.data as Program
        setProgram(p)
        setEditForm({
          name: p.name,
          description: p.description ?? "",
          icon: p.icon,
          status: p.status,
        })
      }
    } catch {
      setError("Failed to load program")
    } finally {
      setLoading(false)
    }
  }

  const fetchParticipants = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.programs[":id"].participants.$get({
        param: { id },
        query: { limit: "100", offset: "0" },
      })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setParticipants(body.data.items as ParticipantListItem[])
      }
    } catch {
      // Silently fail
    }
  }

  const fetchEvents = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.programs[":id"].events.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setEvents(body.data.items as EventListItem[])
      }
    } catch {
      // Silently fail
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchProgram()
      void fetchParticipants()
      void fetchEvents()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(":")
    const h = Number.parseInt(hours || "0", 10)
    const period = h >= 12 ? "PM" : "AM"
    const hour12 = h % 12 || 12
    return `${hour12}:${minutes} ${period}`
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.programs[":id"].$patch({
        param: { id },
        json: {
          name: editForm.name,
          description: editForm.description || undefined,
          icon: editForm.icon,
          status: editForm.status,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Program updated successfully")
      setShowEditModal(false)
      void fetchProgram()
    } catch {
      toast.error("Failed to update program")
    } finally {
      setSaving(false)
    }
  }

  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    if (!participantForm.since) {
      toast.error("Select when the participant joined the program")
      return
    }
    setSaving(true)
    try {
      const res = await client.api.admin.programs.participants.$post({
        json: {
          programId: id,
          firstName: participantForm.firstName,
          lastName: participantForm.lastName,
          email: participantForm.email || undefined,
          phone: participantForm.phone || undefined,
          cohort: participantForm.cohort,
          since: participantForm.since.slice(0, 7),
          attendancePercent: Number(participantForm.attendancePercent),
          notes: participantForm.notes || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Participant added successfully")
      setShowAddParticipantModal(false)
      setParticipantForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        cohort: "",
        since: "",
        attendancePercent: "0",
        notes: "",
      })
      void fetchParticipants()
      void fetchProgram()
    } catch {
      toast.error("Failed to add participant")
    } finally {
      setSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowEditModal(false)
      setShowAddParticipantModal(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Program</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this program.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !program) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/programs")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Program not found"}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/programs")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-white">
              <ProgramIcon icon={program.icon} className="h-8 w-8" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/50">{program.code}</p>
              <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">{program.name}</h1>
              <p className="mt-1 text-sm text-ink/60">{program.description || "No description"}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                    PROGRAM_STATUS_COLORS[program.status].bg
                  } ${PROGRAM_STATUS_COLORS[program.status].text}`}
                >
                  {program.status}
                </span>
                <span className="inline-flex items-center rounded-full bg-forest-100 px-2.5 py-0.5 text-xs font-medium text-forest-700">
                  {program.participantsCount} participants
                </span>
              </div>
            </div>
          </div>

          {canMutate && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAddParticipantModal(true)}
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
              >
                <PlusIcon className="h-4 w-4" />
                Add participant
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-forest-900"
              >
                <EditIcon className="h-4 w-4" />
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Events Section */}
      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="flex items-center justify-between border-b border-gold-500/50 px-5 py-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Upcoming Events</h2>
          <button
            onClick={() => navigate(`/admin/events?programId=${program.id}`)}
            className="text-sm font-medium text-forest-700 hover:text-forest-900"
          >
            View all
          </button>
        </div>
        {events.length === 0 ? (
          <div className="px-5 py-8 text-center text-ink/50">
            No events scheduled.
            {canMutate && (
              <button
                onClick={() => navigate(`/admin/events?programId=${program.id}`)}
                className="ml-2 text-forest-700 underline hover:no-underline"
              >
                Create one
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-ink/10">
            {events.slice(0, 3).map((event) => (
              <div
                key={event.id}
                onClick={() => navigate(`/admin/events/${event.id}`)}
                className="flex cursor-pointer items-center justify-between px-5 py-4 hover:bg-cream-50"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-forest-100 text-forest-700">
                    <span className="text-xs font-medium uppercase">
                      {new Date(event.date).toLocaleDateString("en-GB", { month: "short" })}
                    </span>
                    <span className="text-lg font-bold leading-none">
                      {new Date(event.date).getDate()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-ink">{event.title}</p>
                    <div className="mt-1 flex items-center gap-3 text-xs text-ink/60">
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-3 w-3" />
                        {formatTime(event.startTime)}
                      </span>
                      {event.venue && (
                        <span className="flex items-center gap-1">
                          <MapPinIcon className="h-3 w-3" />
                          {event.venue}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink/70">
                    {EVENT_TYPE_LABELS[event.type]}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                      EVENT_STATUS_COLORS[event.status].bg
                    } ${EVENT_STATUS_COLORS[event.status].text}`}
                  >
                    {event.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Participants Table */}
      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="border-b border-gold-500/50 px-5 py-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Participants</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gold-500/50 bg-cream-50">
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Participant</th>
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Cohort</th>
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Since</th>
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Attendance</th>
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Notes</th>
                <th className="px-4 py-4 text-left text-sm font-bold text-ink">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {participants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-ink/50">
                    No participants yet.
                    {canMutate && (
                      <button
                        onClick={() => setShowAddParticipantModal(true)}
                        className="ml-2 text-forest-700 underline hover:no-underline"
                      >
                        Add one
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                participants.map((participant) => (
                  <tr key={participant.id} className="hover:bg-cream-50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-ink">
                          {participant.firstName} {participant.lastName}
                        </p>
                        {participant.email && (
                          <p className="text-xs text-ink/50">{participant.email}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-ink/70">{participant.cohort}</td>
                    <td className="px-4 py-4 text-sm text-ink/70">{participant.since}</td>
                    <td className="px-4 py-4 text-sm text-ink/70">{participant.attendancePercent}%</td>
                    <td className="px-4 py-4 text-sm text-ink">{participant.notes || "—"}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                          STATUS_COLORS[participant.status].bg
                        } ${STATUS_COLORS[participant.status].text}`}
                      >
                        {participant.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Edit Program</h2>
            <p className="mt-1 text-sm text-ink/60">{program.code}</p>
            <form onSubmit={handleEditSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Name *</span>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Description</span>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <Select
                label="Icon"
                value={editForm.icon === "book" ? "Book (Learning)" : editForm.icon === "scissor" ? "Scissor (Skills)" : "Atom (Empowerment)"}
                onChange={(label) => {
                  const iconMap: Record<string, ProgramIcon> = {
                    "Book (Learning)": "book",
                    "Scissor (Skills)": "scissor",
                    "Atom (Empowerment)": "atom",
                  }
                  setEditForm({ ...editForm, icon: iconMap[label] || "book" })
                }}
                options={["Book (Learning)", "Scissor (Skills)", "Atom (Empowerment)"]}
              />

              <Select
                label="Status"
                value={editForm.status === "active" ? "Active" : "Inactive"}
                onChange={(value) => setEditForm({ ...editForm, status: value === "Active" ? "active" : "inactive" })}
                options={["Active", "Inactive"]}
              />

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Add Participant Modal */}
      {showAddParticipantModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Add Participant</h2>
            <p className="mt-1 text-sm text-ink/60">Add a new participant to {program.name}</p>
            <form onSubmit={handleAddParticipant} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">First Name *</span>
                  <input
                    type="text"
                    required
                    value={participantForm.firstName}
                    onChange={(e) => setParticipantForm({ ...participantForm, firstName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Last Name *</span>
                  <input
                    type="text"
                    required
                    value={participantForm.lastName}
                    onChange={(e) => setParticipantForm({ ...participantForm, lastName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Email</span>
                <input
                  type="email"
                  value={participantForm.email}
                  onChange={(e) => setParticipantForm({ ...participantForm, email: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Phone</span>
                <input
                  type="tel"
                  value={participantForm.phone}
                  onChange={(e) => setParticipantForm({ ...participantForm, phone: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Cohort *</span>
                <input
                  type="text"
                  required
                  placeholder="e.g., Enugu · 2026"
                  value={participantForm.cohort}
                  onChange={(e) => setParticipantForm({ ...participantForm, cohort: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <DatePicker
                label="Since"
                value={participantForm.since}
                onChange={(value) => setParticipantForm({ ...participantForm, since: value })}
                placeholder="Select start date"
              />

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Attendance %</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={participantForm.attendancePercent}
                  onChange={(e) => setParticipantForm({ ...participantForm, attendancePercent: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Notes</span>
                <textarea
                  value={participantForm.notes}
                  onChange={(e) => setParticipantForm({ ...participantForm, notes: e.target.value })}
                  rows={2}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddParticipantModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Adding..." : "Add Participant"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
